import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";
import NexusService from "./NexusService";
import FeatureFlagService from "./FeatureFlagService";

export default class ClientService extends AbstractService {
	static endpoint = ENDPOINTS.clients;

	static clientsApi = NexusService.clientsApi;

	static clientInvitationsApi = NexusService.clientInvitationsApi;

	static clientsFeature = "use_api_core_endpoints_clients";

	static getAll(queryParams, getCancelToken) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			const request = {};
			if (Object.hasOwn(queryParams, "pageNumber")) {
				request.pageNumber = queryParams.pageNumber;
			}
			if (Object.hasOwn(queryParams, "displayCount")) {
				request.displayCount = queryParams.displayCount;
			}
			if (Object.hasOwn(queryParams, "includeInactive")) {
				request.includeInactive = queryParams.includeInactive;
			}
			if (Object.hasOwn(queryParams, "minimalData")) {
				request.minimalData = queryParams.minimalData;
			}
			if (Object.hasOwn(queryParams, "name")) {
				request.name = queryParams.name;
			}
			if (Object.hasOwn(queryParams, "organizationId")) {
				request.organizationId = queryParams.organizationId;
			}
			if (Object.hasOwn(queryParams, "getDetails")) {
				request.getDetails = queryParams.getDetails;
			}
			if (Object.hasOwn(queryParams, "isInvited")) {
				request.isInvited = queryParams.isInvited;
			}
			if (Object.hasOwn(queryParams, "isRKPay")) {
				request.isRKPay = queryParams.isRKPay;
			}
			if (Object.hasOwn(queryParams, "includeExternal")) {
				request.includeExternal = queryParams.includeExternal;
			}
			if (Object.hasOwn(queryParams, "jobStatusFilter")) {
				request.jobStatusFilter = queryParams.jobStatusFilter;
			}

			if (Object.hasOwn(request, "pageNumber")) {
				return this.clientsApi.getClients(request)
				.then((response) => ({ data: response }));
			}

			return this.clientsApi.lookupClient(request)
				.then((response) => ({ data: response }));
		}
		return this.get({
			endpoint: this.endpoint,
			queryParams,
			getCancelToken,
		});
	}

	static getById(id) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			const request = {
				id,
			};
			return this.clientsApi.getClient(request).then(response => {
				const apiResponse = {
					data: response,
				};
				return apiResponse;
			});
		}
		return this.get({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static delete(id) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			return this.clientsApi.deleteClient(id).then((response) => ({ data: response }));
		}
		return this.del({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static getLocations(clientId) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			return this.clientsApi.GetClientLocations(clientId).then((response) => ({ data: response }));
		}
		return this.get({
			endpoint: `${this.endpoint}/${clientId}/locations`,
		});
	}

	static inviteClient(data) {
		if (FeatureFlagService.isEnabled("use_api_core_endpoints_client_invitations")) {
			const request = {
				clientInvitationCreatePayload : {},
			}
			if (Object.hasOwn(data, "clientId")){
				request.clientInvitationCreatePayload.ClientId = data.clientId;
			}
			if (Object.hasOwn(data, "contactId")){
				request.clientInvitationCreatePayload.ContactId = data.contactId;
			}
			if (Object.hasOwn(data, "organizationFee")){
				request.clientInvitationCreatePayload.OrganizationFee = data.organizationFee;
			}
			if (Object.hasOwn(data, "status")){
				request.clientInvitationCreatePayload.Status = data.status;
			}
			return this.clientInvitationsApi.createClientInvitation(request)
			.then(response => ({ data: response }));
		}

		return this.post({
			endpoint: ENDPOINTS.clientInvitations,
			data,
		});
	}

	static save(data, queryParams) {
		const clientId = data.id ? data.id : data.Id;
		const isCreating = clientId === undefined;
		if (FeatureFlagService.isEnabled("use_api_core_endpoints_client")) {
			if (isCreating) {
				const request = {
					createDefaultProject: false,
					clientCreatePayload: {},
				}

				if (Object.hasOwn(data, "Name")) {
					request.clientCreatePayload.Name = data.Name;
				}
				if (Object.hasOwn(data, "IsRKPay")) {
					request.clientCreatePayload.IsRKPay = data.IsRKPay;
				}
				if (Object.hasOwn(data, "IsAgencyRKPayEnabled")) {
					request.clientCreatePayload.IsAgencyRKPayEnabled = data.IsAgencyRKPayEnabled;
				}
				if (Object.hasOwn(data, "BillingContactId")) {
					request.clientCreatePayload.BillingContactId = data.BillingContactId;
				}
				if (Object.hasOwn(data, "ClientLogo")) {
					request.clientCreatePayload.ClientLogo = data.ClientLogo;
				}
				if (Object.hasOwn(data, "OrganizationFee")) {
					request.clientCreatePayload.OrganizationFee = data.OrganizationFee;
				}
				if (Object.hasOwn(data, "IsInvited")) {
					request.clientCreatePayload.IsInvited = data.IsInvited;
				}
				if (Object.hasOwn(data, "SameDayEnabled")) {
					request.clientCreatePayload.SameDayEnabled = data.SameDayEnabled;
				}
				if (Object.hasOwn(data, "FloatInvoicePayEnabled")) {
					request.clientCreatePayload.FloatInvoicePayEnabled = data.FloatInvoicePayEnabled;
				}
				if (Object.hasOwn(data, "JobInvoicedTypeId")) {
					request.clientCreatePayload.JobInvoicedTypeId = data.JobInvoicedTypeId;
				}
				if (Object.hasOwn(data, "IsWorkerCompensationFeeEnabled")) {
					request.clientCreatePayload.IsWorkerCompensationFeeEnabled = data.IsWorkerCompensationFeeEnabled;
				}
				if (Object.hasOwn(data, "RKAccount")) {
					request.clientCreatePayload.RKAccount = data.RKAccount;
				}
				if (Object.hasOwn(data, "AuthorizedToUseDepartmentVehicles")) {
					request.clientCreatePayload.AuthorizedToUseDepartmentVehicles = data.AuthorizedToUseDepartmentVehicles;
				}
				if (Object.hasOwn(data, "AllowExcludeOfficerRate")) {
					request.clientCreatePayload.AllowExcludeOfficerRate = data.AllowExcludeOfficerRate;
				}
				if (Object.hasOwn(data, "TransferOfficerPaymentsToAgency")) {
					request.clientCreatePayload.TransferOfficerPaymentsToAgency = data.TransferOfficerPaymentsToAgency;
				}
				if (Object.hasOwn(data, "EnforceIncludeOfficerRate")) {
					request.clientCreatePayload.EnforceIncludeOfficerRate = data.EnforceIncludeOfficerRate;
				}
				if (Object.hasOwn(data, "EmailAddress")) {
					request.clientCreatePayload.EmailAddress = data.EmailAddress;
				}
				if (Object.hasOwn(data, "EnforceV2NonRKPayInvoicing")) {
					request.clientCreatePayload.EnforceV2NonRKPayInvoicing = data.EnforceV2NonRKPayInvoicing;
				}
				if (Object.hasOwn(data, "PrimaryContactId")) {
					request.clientCreatePayload.PrimaryContactId = data.PrimaryContactId;
				}
				if (Object.hasOwn(data, "EmergencyContactId")) {
					request.clientCreatePayload.EmergencyContactId = data.EmergencyContactId;
				}
				if (Object.hasOwn(data, "IsActive")) {
					request.clientCreatePayload.IsActive = data.IsActive;
				}
				if (Object.hasOwn(data, "TaxExemptionRequest")) {
					request.clientCreatePayload.TaxExemptionRequest = data.TaxExemptionRequest;
				}
				if (Object.hasOwn(data, "ClientCoordinatorIds")) {
					request.clientCreatePayload.ClientCoordinatorIds = data.ClientCoordinatorIds;
				}
				if (Object.hasOwn(data, "ClientInvoiceDisclaimer")) {
					request.clientCreatePayload.ClientInvoiceDisclaimer = data.ClientInvoiceDisclaimer;
				}
				if (Object.hasOwn(data, "CloudFileIds")) {
					request.clientCreatePayload.CloudFileIds = data.CloudFileIds;
				}
				return this.clientsApi.createClient(request).then(response => ({data: response}));
			}
			const request = {
				id: data.Id,
				clientUpdatePayload: {},
			}

			if (Object.hasOwn(data, "Name")) {
				request.clientUpdatePayload.Name = data.Name;
			}
			if (Object.hasOwn(data, "IsRKPay")) {
				request.clientUpdatePayload.IsRKPay = data.IsRKPay;
			}
			if (Object.hasOwn(data, "IsAgencyRKPayEnabled")) {
				request.clientUpdatePayload.IsAgencyRKPayEnabled = data.IsAgencyRKPayEnabled;
			}
			if (Object.hasOwn(data, "BillingContactId")) {
				request.clientUpdatePayload.BillingContactId = data.BillingContactId;
			}
			if (Object.hasOwn(data, "ClientLogo")) {
				request.clientUpdatePayload.ClientLogo = data.ClientLogo;
			}
			if (Object.hasOwn(data, "OrganizationFee")) {
				request.clientUpdatePayload.OrganizationFee = data.OrganizationFee;
			}
			if (Object.hasOwn(data, "IsInvited")) {
				request.clientUpdatePayload.IsInvited = data.IsInvited;
			}
			if (Object.hasOwn(data, "SameDayEnabled")) {
				request.clientUpdatePayload.SameDayEnabled = data.SameDayEnabled;
			}
			if (Object.hasOwn(data, "FloatInvoicePayEnabled")) {
				request.clientUpdatePayload.FloatInvoicePayEnabled = data.FloatInvoicePayEnabled;
			}
			if (Object.hasOwn(data, "JobInvoicedTypeId")) {
				request.clientUpdatePayload.JobInvoicedTypeId = data.JobInvoicedTypeId;
			}
			if (Object.hasOwn(data, "IsWorkerCompensationFeeEnabled")) {
				request.clientUpdatePayload.IsWorkerCompensationFeeEnabled = data.IsWorkerCompensationFeeEnabled;
			}
			if (Object.hasOwn(data, "RKAccount")) {
				request.clientUpdatePayload.RKAccount = data.RKAccount;
			}
			if (Object.hasOwn(data, "AuthorizedToUseDepartmentVehicles")) {
				request.clientUpdatePayload.AuthorizedToUseDepartmentVehicles = data.AuthorizedToUseDepartmentVehicles;
			}
			if (Object.hasOwn(data, "AllowExcludeOfficerRate")) {
				request.clientUpdatePayload.AllowExcludeOfficerRate = data.AllowExcludeOfficerRate;
			}
			if (Object.hasOwn(data, "TransferOfficerPaymentsToAgency")) {
				request.clientUpdatePayload.TransferOfficerPaymentsToAgency = data.TransferOfficerPaymentsToAgency;
			}
			if (Object.hasOwn(data, "EnforceIncludeOfficerRate")) {
				request.clientUpdatePayload.EnforceIncludeOfficerRate = data.EnforceIncludeOfficerRate;
			}
			if (Object.hasOwn(data, "EmailAddress")) {
				request.clientUpdatePayload.EmailAddress = data.EmailAddress;
			}
			if (Object.hasOwn(data, "EnforceV2NonRKPayInvoicing")) {
				request.clientUpdatePayload.EnforceV2NonRKPayInvoicing = data.EnforceV2NonRKPayInvoicing;
			}
			if (Object.hasOwn(data, "PrimaryContactId")) {
				request.clientUpdatePayload.PrimaryContactId = data.PrimaryContactId;
			}
			if (Object.hasOwn(data, "EmergencyContactId")) {
				request.clientUpdatePayload.EmergencyContactId = data.EmergencyContactId;
			}
			if (Object.hasOwn(data, "IsActive")) {
				request.clientUpdatePayload.IsActive = data.IsActive;
			}
			if (Object.hasOwn(data, "TaxExemptionRequest")) {
				request.clientUpdatePayload.TaxExemptionRequest = data.TaxExemptionRequest;
			}
			if (Object.hasOwn(data, "ClientCoordinatorIds")) {
				request.clientUpdatePayload.ClientCoordinatorIds = data.ClientCoordinatorIds;
			}
			if (Object.hasOwn(data, "ClientInvoiceDisclaimer")) {
				request.clientUpdatePayload.ClientInvoiceDisclaimer = data.ClientInvoiceDisclaimer;
			}
			if (Object.hasOwn(data, "CloudFileIds")) {
				request.clientUpdatePayload.CloudFileIds = data.CloudFileIds;
			}
			return this.clientsApi.updateClient(request).then(response => ({data: response}));
		}

		const endpoint = `${this.endpoint}${!isCreating ? `/${clientId}` : ""}`;

		return this.request({
			endpoint,
			method: isCreating ? "POST" : "PUT",
			data,
			queryParams,
		});
	}

	static getPosts({ id, pagingToken, displayCount, pageNumber }) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/posts`,
			queryParams: {
				pagingToken,
				displayCount,
				pageNumber,
			},
		});
	}

	static uploadClientImage({ id, file }) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			return this.clientsApi.postClientImage(file, id).then((response) => ({ data: response }));
		}
		const data = new FormData();
		data.append("file", file);

		return this.post({
			endpoint: `${this.endpoint}/${id}/picture`,
			data,
		});
	}

	static uploadTaxExemptionDocument(id, fileData) {
		const data = new FormData();
		data.append("file", fileData);

		return this.post({
			endpoint: `${this.endpoint}/${id}/taxexemption`,
			data,
		});
	}

	static getProcessingFees(clientId, queryParams) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			const request = {
				clientId,
				active: queryParams.active,
			}
			return this.clientsApi.getClientProcessingFees(request).then((response) => ({ data: response }));
		}
		return this.get({
			endpoint: `${this.endpoint}/${clientId}/processingfees`,
			queryParams,
		});
	}

	static updateProcessingFees(clientId, data) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			const request = {
				clientId,
				processingFeeUpdatePayload: {},
			}
			if (Object.hasOwn(data, "Name")) {
				request.processingFeeUpdatePayload.Name = data.Name;
			}
			if (Object.hasOwn(data, "ProcessingFeeId")) {
				request.processingFeeUpdatePayload.ProcessingFeeId = data.ProcessingFeeId;
			}
			if (Object.hasOwn(data, "Rate")) {
				request.processingFeeUpdatePayload.Rate = data.Rate;
			}
			if (Object.hasOwn(data, "EffectiveFrom")) {
				request.processingFeeUpdatePayload.EffectiveFrom = data.EffectiveFrom;
			}
			if (Object.hasOwn(data, "Modifier")) {
				request.processingFeeUpdatePayload.Modifier = data.Modifier;
			}
			return this.clientsApi.updateClientProcessingFee(request).then((response) => ({ data: response }));
		}
		return this.put({
			endpoint: `${this.endpoint}/${clientId}/processingfees`,
			data,
		});
	}

	static getServiceFees(clientId, queryParams) {
		return this.get({
			endpoint: `${this.endpoint}/${clientId}/servicefees`,
			queryParams,
		});
	}

	static updateServiceFees(clientId, data) {
		return this.post({
			endpoint: `${this.endpoint}/${clientId}/servicefees`,
			data,
		});
	}

	static hasFutureOfficerInvoicingJobs(clientId) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			return this.clientsApi.clientHasFutureOfficerJobs({ clientId })
				.then((response) =>({ data: response }));
		}
		return this.get({
			endpoint: `${this.endpoint}/${clientId}/hasFutureOfficerJobs`,
		});
	}

	static sendPaymentReminder(clientId, data) {
		if (FeatureFlagService.isEnabled(this.clientsFeature)) {
			const request = {
				clientId,
				sendPaymentReminderPayload: {},
			}
			if (Object.hasOwn(data, "CC")) {
				request.sendPaymentReminderPayload.CC = data.CC;
			}
			if (Object.hasOwn(data, "BCC")) {
				request.sendPaymentReminderPayload.BCC = data.BCC;
			}
			if (Object.hasOwn(data, "SendEmailToMyself")) {
				request.sendPaymentReminderPayload.SendEmailToMyself = data.SendEmailToMyself;
			}
			if (Object.hasOwn(data, "InvoiceId")) {
				request.sendPaymentReminderPayload.InvoiceId = data.InvoiceId;
			}
			if (Object.hasOwn(data, "BillToEmail")) {
				request.sendPaymentReminderPayload.BillToEmail = data.BillToEmail;
			}
			if (Object.hasOwn(data, "BillToName")) {
				request.sendPaymentReminderPayload.BillToName = data.BillToName;
			}
			return this.clientsApi.sendPaymentReminder(request).then((response) => ({ data: response }));
		}
		return this.put({
			endpoint: `${this.endpoint}/${clientId}/sendpaymentreminder`,
			data,
		});
	}
}
